import React, { useCallback, useEffect, useState } from "react";
import {
    Card,
    Table,
    Tag,
    Button,
    Space,
    Select,
    message,
    Drawer,
    Descriptions,
    Avatar,
    Popconfirm,
    Typography,
    Empty,
} from "antd";
import {
    ReloadOutlined,
    UserOutlined,
    DeleteOutlined,
    StopOutlined,
    CheckOutlined,
    CloseOutlined,
} from "@ant-design/icons";
import { api } from "../api";
import { fmtDate } from "../utils";

const { Paragraph, Text } = Typography;

// Human labels for the mobile app's report reason codes.
const REASON_LABELS = {
    spam: "Spam",
    harassment: "Harassment",
    hate: "Hate speech",
    violence: "Violence",
    sexual: "Sexual content",
    other: "Other",
};

const STATUS_TAG = {
    pending: <Tag color="gold">Pending</Tag>,
    reviewed: <Tag color="blue">Reviewed</Tag>,
    dismissed: <Tag>Dismissed</Tag>,
    actioned: <Tag color="green">Actioned</Tag>,
};

const KIND_TAG = {
    report: <Tag color="red">Report</Tag>,
    block: <Tag color="purple">Block</Tag>,
};

const CONTENT_TYPE_LABELS = {
    comment: "Comment",
    post: "Post",
    message: "Message",
    user: "User",
};

// Small reporter/target profile cell.
const UserCell = ({ user, fallback = "—" }) => {
    if (!user) return <span style={{ color: "#94a3b8" }}>{fallback}</span>;
    return (
        <Space size={8}>
            <Avatar size="small" src={user.avatar} icon={<UserOutlined />} />
            <div>
                <div>{user.name || "Unknown"}</div>
                {user.email && (
                    <div className="admin-cell-sub" style={{ color: "#64748b" }}>
                        {user.email}
                    </div>
                )}
            </div>
        </Space>
    );
};

const ReportsPage = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({
        status: "pending",
        kind: "",
        reason: "",
    });

    // Detail drawer state.
    const [detail, setDetail] = useState(null); // { report, content }
    const [detailLoading, setDetailLoading] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.listReports({
                status: filters.status,
                kind: filters.kind,
                reason: filters.reason,
                limit: 100,
            });
            setItems(res.items || []);
        } catch (e) {
            message.error(e.message);
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        load();
    }, [load]);

    const openDetail = async (id) => {
        setDrawerOpen(true);
        setDetailLoading(true);
        setDetail(null);
        try {
            const res = await api.getReport(id);
            setDetail(res);
        } catch (e) {
            message.error(e.message);
        } finally {
            setDetailLoading(false);
        }
    };

    // Update a report's status both in the list and (if open) the drawer.
    const applyUpdate = (updated) => {
        setItems((prev) => {
            // If a status filter is active and the row no longer matches, drop it.
            if (filters.status && updated.status !== filters.status) {
                return prev.filter((it) => it._id !== updated._id);
            }
            return prev.map((it) => (it._id === updated._id ? updated : it));
        });
        setDetail((d) =>
            d && d.report._id === updated._id
                ? { ...d, report: { ...d.report, ...updated } }
                : d
        );
    };

    const setStatus = async (id, status) => {
        try {
            const updated = await api.setReportStatus(id, status);
            applyUpdate(updated);
            message.success(`Marked ${status}`);
        } catch (e) {
            message.error(e.message);
        }
    };

    const deleteContent = async (id) => {
        try {
            const res = await api.deleteReportedContent(id);
            message.success(
                res.deleted ? "Content deleted" : "Content already gone"
            );
            setStatusLocal(id, "actioned");
        } catch (e) {
            message.error(e.message);
        }
    };

    const banTarget = async (id) => {
        try {
            await api.banReportTarget(id, true);
            message.success("Target user disabled");
            setStatusLocal(id, "actioned");
        } catch (e) {
            message.error(e.message);
        }
    };

    // After an action endpoint changes status server-side, reflect it locally
    // without a second round-trip.
    const setStatusLocal = (id, status) => {
        setItems((prev) => {
            if (filters.status && status !== filters.status) {
                return prev.filter((it) => it._id !== id);
            }
            return prev.map((it) => (it._id === id ? { ...it, status } : it));
        });
        setDetail((d) =>
            d && d.report._id === id
                ? { ...d, report: { ...d.report, status } }
                : d
        );
    };

    const columns = [
        {
            title: "Type",
            dataIndex: "kind",
            key: "kind",
            width: 90,
            render: (k) => KIND_TAG[k] || <Tag>{k}</Tag>,
        },
        {
            title: "Reporter",
            key: "reporter",
            render: (_, r) => <UserCell user={r.reporter} />,
        },
        {
            title: "Target",
            key: "targetUser",
            render: (_, r) => <UserCell user={r.targetUser} />,
        },
        {
            title: "Content",
            dataIndex: "contentType",
            key: "contentType",
            width: 100,
            render: (c) =>
                c ? (
                    <Tag>{CONTENT_TYPE_LABELS[c] || c}</Tag>
                ) : (
                    <span style={{ color: "#94a3b8" }}>—</span>
                ),
        },
        {
            title: "Reason",
            dataIndex: "reason",
            key: "reason",
            width: 130,
            render: (r) =>
                r ? (
                    REASON_LABELS[r] || r
                ) : (
                    <span style={{ color: "#94a3b8" }}>—</span>
                ),
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            width: 110,
            render: (s) => STATUS_TAG[s] || <Tag>{s}</Tag>,
        },
        {
            title: "Time",
            dataIndex: "createdAt",
            key: "createdAt",
            width: 160,
            render: (d) => fmtDate(d, true),
        },
        {
            title: "Actions",
            key: "actions",
            width: 120,
            render: (_, r) => (
                <Button size="small" onClick={() => openDetail(r._id)}>
                    Review
                </Button>
            ),
        },
    ];

    const report = detail?.report;
    const content = detail?.content;
    const isBlock = report?.kind === "block";
    const canDeleteContent =
        report &&
        report.contentId &&
        ["comment", "post", "message"].includes(report.contentType);

    return (
        <div>
            <h1 className="admin-page-title">Moderation / Reports</h1>
            <Card
                variant="borderless"
                title="User reports & blocks"
                extra={
                    <Button
                        size="small"
                        icon={<ReloadOutlined />}
                        loading={loading}
                        onClick={load}
                    >
                        Refresh
                    </Button>
                }
            >
                <Space wrap style={{ marginBottom: 16 }}>
                    <Select
                        value={filters.status}
                        style={{ width: 150 }}
                        onChange={(v) =>
                            setFilters((f) => ({ ...f, status: v }))
                        }
                        options={[
                            { value: "pending", label: "Pending" },
                            { value: "reviewed", label: "Reviewed" },
                            { value: "dismissed", label: "Dismissed" },
                            { value: "actioned", label: "Actioned" },
                            { value: "", label: "All statuses" },
                        ]}
                    />
                    <Select
                        value={filters.kind}
                        style={{ width: 150 }}
                        onChange={(v) => setFilters((f) => ({ ...f, kind: v }))}
                        options={[
                            { value: "", label: "All kinds" },
                            { value: "report", label: "Reports only" },
                            { value: "block", label: "Blocks only" },
                        ]}
                    />
                    <Select
                        value={filters.reason}
                        style={{ width: 160 }}
                        onChange={(v) =>
                            setFilters((f) => ({ ...f, reason: v }))
                        }
                        options={[
                            { value: "", label: "All reasons" },
                            ...Object.entries(REASON_LABELS).map(
                                ([value, label]) => ({ value, label })
                            ),
                        ]}
                    />
                </Space>

                <Table
                    rowKey="_id"
                    size="small"
                    loading={loading}
                    columns={columns}
                    dataSource={items}
                    scroll={{ x: "max-content" }}
                    pagination={{ pageSize: 15 }}
                />
            </Card>

            <Drawer
                title="Report detail"
                width={520}
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                loading={detailLoading}
            >
                {report && (
                    <Space
                        direction="vertical"
                        size="large"
                        style={{ width: "100%" }}
                    >
                        {isBlock && (
                            <Tag color="purple">
                                Block log entry — informational, not necessarily
                                actionable
                            </Tag>
                        )}

                        <Descriptions
                            column={1}
                            size="small"
                            bordered
                            items={[
                                {
                                    key: "kind",
                                    label: "Kind",
                                    children: KIND_TAG[report.kind],
                                },
                                {
                                    key: "status",
                                    label: "Status",
                                    children: STATUS_TAG[report.status],
                                },
                                {
                                    key: "reason",
                                    label: "Reason",
                                    children:
                                        REASON_LABELS[report.reason] ||
                                        report.reason ||
                                        "—",
                                },
                                {
                                    key: "contentType",
                                    label: "Content type",
                                    children: report.contentType
                                        ? CONTENT_TYPE_LABELS[
                                              report.contentType
                                          ] || report.contentType
                                        : "—",
                                },
                                {
                                    key: "note",
                                    label: "Note",
                                    children: report.note || "—",
                                },
                                {
                                    key: "time",
                                    label: "Reported at",
                                    children: fmtDate(report.createdAt, true),
                                },
                            ]}
                        />

                        <div>
                            <Text strong>Reporter</Text>
                            <div style={{ marginTop: 8 }}>
                                <UserCell user={report.reporter} />
                            </div>
                        </div>

                        <div>
                            <Text strong>
                                {isBlock ? "Blocked user" : "Target user"}
                            </Text>
                            <div style={{ marginTop: 8 }}>
                                <UserCell
                                    user={report.targetUser}
                                    fallback="No target user"
                                />
                            </div>
                        </div>

                        <div>
                            <Text strong>Reported content</Text>
                            <div style={{ marginTop: 8 }}>
                                {content ? (
                                    <Card size="small">
                                        {content.title && (
                                            <Paragraph strong style={{ marginBottom: 4 }}>
                                                {content.title}
                                            </Paragraph>
                                        )}
                                        {content.content && (
                                            <Paragraph style={{ marginBottom: 4 }}>
                                                {content.content}
                                            </Paragraph>
                                        )}
                                        {content.text && (
                                            <Paragraph style={{ marginBottom: 4 }}>
                                                {content.text}
                                            </Paragraph>
                                        )}
                                        {content.name && (
                                            <Paragraph style={{ marginBottom: 4 }}>
                                                {content.name}
                                                {content.email
                                                    ? ` · ${content.email}`
                                                    : ""}
                                            </Paragraph>
                                        )}
                                        {content.createdAt && (
                                            <Text
                                                type="secondary"
                                                style={{ fontSize: 12 }}
                                            >
                                                {fmtDate(content.createdAt, true)}
                                            </Text>
                                        )}
                                    </Card>
                                ) : (
                                    <Empty
                                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                                        description={
                                            report.contentId
                                                ? "Content not found (deleted or unavailable)"
                                                : "No linked content"
                                        }
                                    />
                                )}
                            </div>
                        </div>

                        {/* Workflow + actions */}
                        <Space direction="vertical" style={{ width: "100%" }}>
                            <Text strong>Actions</Text>
                            <Space wrap>
                                <Button
                                    icon={<CheckOutlined />}
                                    onClick={() =>
                                        setStatus(report._id, "reviewed")
                                    }
                                    disabled={report.status === "reviewed"}
                                >
                                    Mark reviewed
                                </Button>
                                <Button
                                    icon={<CloseOutlined />}
                                    onClick={() =>
                                        setStatus(report._id, "dismissed")
                                    }
                                    disabled={report.status === "dismissed"}
                                >
                                    Dismiss
                                </Button>
                            </Space>
                            <Space wrap>
                                {canDeleteContent && (
                                    <Popconfirm
                                        title="Delete this content permanently?"
                                        onConfirm={() =>
                                            deleteContent(report._id)
                                        }
                                    >
                                        <Button danger icon={<DeleteOutlined />}>
                                            Delete content
                                        </Button>
                                    </Popconfirm>
                                )}
                                {report.targetUser && (
                                    <Popconfirm
                                        title="Disable (ban) the target user?"
                                        onConfirm={() => banTarget(report._id)}
                                    >
                                        <Button danger icon={<StopOutlined />}>
                                            Ban target user
                                        </Button>
                                    </Popconfirm>
                                )}
                            </Space>
                        </Space>
                    </Space>
                )}
            </Drawer>
        </div>
    );
};

export default ReportsPage;
